import * as API from 'aws-amplify/api';
import { ConsoleLogger as Logger } from 'aws-amplify/utils';
import { notification } from 'antd';
import { createErrorMessage } from './globalStateHelper';

const log = new Logger('context:deposits');

async function updateCapacitySums() {
  const { isAdmin, isReadOnlyAdmin, selectedMarketPartyId } = this.state;
  const url = isAdmin() || isReadOnlyAdmin()
    ? '/admin/marketparties/capacities/totals'
    : `/marketparties/${selectedMarketPartyId}/capacities/totals`;

  try {
    const { body } = await API.get({
      apiName: 'FINTSO',
      path: url,
    }).response;
    const { items } = await body.json();

    const capacitySums = isAdmin() || isReadOnlyAdmin()
      ? items
      : [{ marketPartyId: selectedMarketPartyId, totals: items }];
    this.setState({ capacitySums });
    log.info('capacity sums updated with data:', capacitySums);
  } catch (error) {
    this.setState({ capacitySums: [] });
    const { t } = this.props;
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorFetchingCapacitySums'),
      description: await createErrorMessage(error),
    });
    log.error('error fetching capacity sums:', await createErrorMessage(error, true));
  }
}

async function updateDeposits() {
  try {
    const { body } = await API.get({
      apiName: 'FINTSO',
      path: '/admin/deposits',
    }).response;
    const { items: deposits } = await body.json();
    this.setState({ deposits });
    log.info('deposits updated with data:', deposits);
  } catch (error) {
    this.setState({ deposits: [] });
    const { t } = this.props;
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorFetchingDeposits'),
      description: await createErrorMessage(error),
    });
    log.error('error fetching deposits:', await createErrorMessage(error, true));
  }
}

async function updateMarketPartyDepositSummary(marketPartyId) {
  this.setState({ depositSummaryLoading: true });
  const {
    selectedMarketPartyId,
    marketPartyDepositSummary: oldMarketPartyDepositSummary,
  } = this.state;
  try {
    const { body } = await API.get({
      apiName: 'FINTSO',
      path: `/marketparties/${marketPartyId}/deposits-v2`,
    }).response;
    const depositSummary = await body.json();
    if (selectedMarketPartyId === marketPartyId) {
      this.setState({ depositSummary });
    }
    const marketPartyDepositSummary = {
      ...oldMarketPartyDepositSummary,
      [marketPartyId]: depositSummary,
    };
    this.setState({ marketPartyDepositSummary });
  } catch (error) {
    if (selectedMarketPartyId === marketPartyId) {
      this.setState({ depositSummary: {} });
    }
    const marketPartyDepositSummary = {
      ...oldMarketPartyDepositSummary,
      [marketPartyId]: undefined,
    };
    this.setState({ marketPartyDepositSummary });

    const { t } = this.props;
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorFetchingDepositSummary'),
      description: await createErrorMessage(error),
    });
    log.error('error fetching deposit summary:', await createErrorMessage(error, true));
  }
  this.setState({ depositSummaryLoading: false });
}

async function saveDeposit(marketPartyId, amount, start, end) {
  const { deposits } = this.state;
  const existingDeposit = deposits.find(item => item.marketPartyId === marketPartyId);

  try {
    const depositBody = {
      amount,
      marketPartyId,
      start,
      end,
    };
    const { body } = await API.post({
      apiName: 'FINTSO',
      path: '/admin/deposits',
      options: { body: depositBody },
    }).response;
    const deposit = await body.json();

    if (!existingDeposit) {
      deposits.push(deposit);
    } else {
      Object.assign(existingDeposit, deposit);
    }

    this.setState({ deposits });
    log.info('deposit saved:', deposit);
    log.info('deposits updated with data:', deposits);
  } catch (error) {
    const { t } = this.props;
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorSavingDeposit'),
      description: await createErrorMessage(error),
    });
    log.error('error saving deposit:', await createErrorMessage(error, true));
  }
}

async function updateImbalanceEstimates() {
  try {
    const { body } = await API.get({
      apiName: 'FINTSO',
      path: '/admin/deposits/imbalanceestimates',
    }).response;
    const { items: imbalanceEstimates } = await body.json();
    this.setState({ imbalanceEstimates });
    log.info('updated imbalance estimates with data:', imbalanceEstimates);
  } catch (error) {
    this.setState({ imbalanceEstimates: [] });
    const { t } = this.props;
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorFetchingImbalanceEstimates'),
      description: await createErrorMessage(error),
    });
    log.error('error fetching imbalance estimates:', await createErrorMessage(error));
  }
}

async function updateInvoicedTaxes() {
  try {
    const { body } = await API.get({
      apiName: 'FINTSO',
      path: '/admin/deposits/invoicedtaxes',
    }).response;
    const { items: invoicedTaxes } = await body.json();
    this.setState({ invoicedTaxes });
    log.info('updated invoiced taxes with data:', invoicedTaxes);
  } catch (error) {
    this.setState({ invoicedTaxes: [] });
    const { t } = this.props;
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorFetchingInvoicedTaxes'),
      description: await createErrorMessage(error),
    });
    log.error('error fetching invoiced taxes:', await createErrorMessage(error));
  }
}

const depositsContext = {
  capacitySums: [],
  imbalanceEstimates: [],
  invoicedTaxes: [],
  deposits: [],
  depositSummary: {},
  depositSummaryLoading: false,
  saveDeposit: () => { },
  updateCapacitySums: () => {},
  updateMarketPartyDepositSummary: () => {},
  updateDeposits: () => {},
  updateImbalanceEstimates: () => { },
  updateInvoicedTaxes: () => {},
};

const functions = {
  saveDeposit,
  updateCapacitySums,
  updateMarketPartyDepositSummary,
  updateDeposits,
  updateImbalanceEstimates,
  updateInvoicedTaxes,
};

const deposits = {
  ...depositsContext,
  ...functions,
};

export {
  depositsContext,
  deposits,
};
